/**
 * VideoClipEditor Component
 *
 * Cuts one continuous video into several separate clips. The user scrubs the source, marks
 * out a window, names it, and repeats — nothing is written until they save the whole batch.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDialogFocus } from "../../../../../hooks/useDialogFocus";
import { useVideoClipping } from '../../../../../hooks/useVideoClipping';
import { getValidatedGcsUrl } from '../../../../../utils/url-validation';
import {
  MAX_CLIPS_PER_REQUEST,
  MIN_CLIP_DURATION_SECONDS,
  formatClipTime,
  validateClipWindow,
} from '../../../../../utils/video-clip-rules';
import type { PendingClip } from '../../../../../types/prospect-media';
import { ModalCloseButton } from "../../../../../components/ModalCloseButton";
import './VideoClipEditor.css';

interface VideoClipEditorProps {
  prospectId: string;
  videoId: string;
  /** Duration reported by the API, used until the media element reports its own. */
  fallbackDurationSeconds?: number | null;
  getVideoUrl: (videoId: string) => Promise<string | null>;
  onClose: () => void;
  /** Called after clips are saved so the caller can refresh its lists. */
  onClipsCreated: (clipCount: number) => void;
}

/** Slider granularity. Finer than this is more precision than a mouse drag can express. */
const SCRUB_STEP_SECONDS = 0.1;

export function VideoClipEditor({
  prospectId,
  videoId,
  fallbackDurationSeconds,
  getVideoUrl,
  onClose,
  onClipsCreated,
}: VideoClipEditorProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useDialogFocus({ isOpen: true, dialogRef, handleEscape: false });

  const videoRef = useRef<HTMLVideoElement>(null);
  const { saveClips, isSaving } = useVideoClipping(prospectId);

  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [duration, setDuration] = useState(fallbackDurationSeconds ?? 0);
  const [startSeconds, setStartSeconds] = useState(0);
  const [endSeconds, setEndSeconds] = useState(0);
  const [clipName, setClipName] = useState('');

  const [pendingClips, setPendingClips] = useState<PendingClip[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Bounds the preview: while set, playback stops when it reaches this time.
  const previewEndRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const url = await getVideoUrl(videoId);
        if (cancelled) return;

        const validatedUrl = getValidatedGcsUrl(url);
        if (!validatedUrl) {
          console.error('[VideoClipEditor] Invalid or non-GCS URL', { videoId, receivedUrl: url });
          setLoadError('Invalid video URL. Please contact support.');
          return;
        }
        setVideoUrl(validatedUrl);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : 'Failed to load video');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [videoId, getVideoUrl]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;

    setDuration(video.duration);
    // Open with the whole video selected: the common first move is trimming inward.
    setStartSeconds(0);
    setEndSeconds(video.duration);
  }, []);

  // Stops preview playback at the clip's end instead of running on into the next play.
  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    const previewEnd = previewEndRef.current;
    if (!video || previewEnd === null) return;

    if (video.currentTime >= previewEnd) {
      video.pause();
      previewEndRef.current = null;
    }
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    previewEndRef.current = null;
    video.currentTime = seconds;
  }, []);

  const handleSetStartToPlayhead = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const next = Math.min(video.currentTime, Math.max(0, endSeconds - MIN_CLIP_DURATION_SECONDS));
    setStartSeconds(next);
    setError(null);
  }, [endSeconds]);

  const handleSetEndToPlayhead = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    const next = Math.max(video.currentTime, startSeconds + MIN_CLIP_DURATION_SECONDS);
    setEndSeconds(Math.min(next, duration || next));
    setError(null);
  }, [startSeconds, duration]);

  const handlePreview = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    previewEndRef.current = endSeconds;
    video.currentTime = startSeconds;
    void video.play();
  }, [startSeconds, endSeconds]);

  const handleStartChange = useCallback(
    (value: number) => {
      // Keep the handles from crossing — an inverted window is never a valid clip. Seek to the
      // clamped value, not the raw one, so the frame on screen is the frame being marked.
      const clamped = Math.min(value, Math.max(0, endSeconds - MIN_CLIP_DURATION_SECONDS));
      setStartSeconds(clamped);
      setError(null);
      seekTo(clamped);
    },
    [endSeconds, seekTo]
  );

  const handleEndChange = useCallback(
    (value: number) => {
      const clamped = Math.max(value, startSeconds + MIN_CLIP_DURATION_SECONDS);
      setEndSeconds(clamped);
      setError(null);
      seekTo(clamped);
    },
    [startSeconds, seekTo]
  );

  const handleAddClip = useCallback(() => {
    const problem = validateClipWindow(startSeconds, endSeconds, duration);
    if (problem) {
      setError(problem);
      return;
    }

    if (pendingClips.length >= MAX_CLIPS_PER_REQUEST) {
      setError(`You can add up to ${MAX_CLIPS_PER_REQUEST} clips at a time. Save these first.`);
      return;
    }

    setPendingClips((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: clipName.trim(),
        startSeconds,
        endSeconds,
      },
    ]);
    setClipName('');
    setError(null);
    setMessage(null);
  }, [startSeconds, endSeconds, duration, clipName, pendingClips.length]);

  const handleRemoveClip = useCallback((id: string) => {
    setPendingClips((prev) => prev.filter((clip) => clip.id !== id));
  }, []);

  const handleSave = useCallback(async () => {
    if (!videoUrl) return;

    setError(null);
    setMessage(null);

    const result = await saveClips(videoId, videoUrl, pendingClips);

    if (!result.success) {
      setError(result.error || 'Failed to save clips. Please try again.');
      return;
    }

    onClipsCreated(result.clipCount);

    if (result.thumbnailsFailed) {
      // The clips exist and play; only their preview images are missing.
      setMessage(
        `Saved ${result.clipCount} clip${result.clipCount === 1 ? '' : 's'}, but preview images could not be generated.`
      );
      setPendingClips([]);
      return;
    }

    onClose();
  }, [videoUrl, videoId, pendingClips, saveClips, onClipsCreated, onClose]);

  const selectionDuration = Math.max(0, endSeconds - startSeconds);
  const sliderMax = duration || fallbackDurationSeconds || 0;

  // Percentage geometry for the selection band drawn over the timeline.
  const selectionStyle = useMemo(() => {
    if (!sliderMax) return { left: '0%', width: '0%' };
    return {
      left: `${(startSeconds / sliderMax) * 100}%`,
      width: `${(selectionDuration / sliderMax) * 100}%`,
    };
  }, [startSeconds, selectionDuration, sliderMax]);

  return (
    <div className="clip-editor__overlay" onClick={onClose}>
      <div ref={dialogRef} tabIndex={-1}
        className="clip-editor"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Clip video"
      >
        <div className="clip-editor__header">
          <h3 className="clip-editor__title">Clip Video</h3>
          <ModalCloseButton onClick={onClose} label="clip editor" />
        </div>

        {isLoading && <div className="clip-editor__status">Loading video...</div>}
        {loadError && <div className="clip-editor__error">{loadError}</div>}

        {videoUrl && !isLoading && !loadError && (
          <>
            <video
              ref={videoRef}
              className="clip-editor__video"
              src={videoUrl}
              controls
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onError={() => setLoadError('Unable to play video')}
            />

            <div className="clip-editor__timeline">
              <div className="clip-editor__track">
                <div className="clip-editor__selection" style={selectionStyle} />
              </div>

              <label className="clip-editor__slider-row">
                <span className="clip-editor__slider-label">Start</span>
                <input
                  type="range"
                  min={0}
                  max={sliderMax}
                  step={SCRUB_STEP_SECONDS}
                  value={startSeconds}
                  onChange={(e) => handleStartChange(Number(e.target.value))}
                  aria-label="Clip start time"
                />
                <span className="clip-editor__time">{formatClipTime(startSeconds)}</span>
                <button
                  type="button"
                  className="clip-editor__playhead-button"
                  onClick={handleSetStartToPlayhead}
                >
                  Use playhead
                </button>
              </label>

              <label className="clip-editor__slider-row">
                <span className="clip-editor__slider-label">End</span>
                <input
                  type="range"
                  min={0}
                  max={sliderMax}
                  step={SCRUB_STEP_SECONDS}
                  value={endSeconds}
                  onChange={(e) => handleEndChange(Number(e.target.value))}
                  aria-label="Clip end time"
                />
                <span className="clip-editor__time">{formatClipTime(endSeconds)}</span>
                <button
                  type="button"
                  className="clip-editor__playhead-button"
                  onClick={handleSetEndToPlayhead}
                >
                  Use playhead
                </button>
              </label>
            </div>

            <div className="clip-editor__controls">
              <input
                type="text"
                className="clip-editor__name-input"
                placeholder="Clip name (optional)"
                value={clipName}
                maxLength={255}
                onChange={(e) => setClipName(e.target.value)}
                aria-label="Clip name"
              />
              <button type="button" className="clip-editor__secondary" onClick={handlePreview}>
                Preview
              </button>
              <button type="button" className="clip-editor__primary" onClick={handleAddClip}>
                Add clip ({formatClipTime(selectionDuration)})
              </button>
            </div>

            {error && <div className="clip-editor__error">{error}</div>}
            {message && <div className="clip-editor__message">{message}</div>}

            {pendingClips.length > 0 && (
              <ul className="clip-editor__pending-list">
                {pendingClips.map((clip, index) => (
                  <li key={clip.id} className="clip-editor__pending-item">
                    <span className="clip-editor__pending-name">
                      {clip.title || `Clip ${index + 1}`}
                    </span>
                    <span className="clip-editor__pending-range">
                      {formatClipTime(clip.startSeconds)} – {formatClipTime(clip.endSeconds)}
                    </span>
                    <button
                      type="button"
                      className="clip-editor__pending-remove"
                      onClick={() => handleRemoveClip(clip.id)}
                      aria-label={`Remove ${clip.title || `clip ${index + 1}`}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="clip-editor__footer">
              <span className="clip-editor__count">
                {pendingClips.length === 0
                  ? 'No clips added yet'
                  : `${pendingClips.length} clip${pendingClips.length === 1 ? '' : 's'} ready to save`}
              </span>
              <button
                type="button"
                className="clip-editor__primary"
                disabled={pendingClips.length === 0 || isSaving}
                onClick={handleSave}
              >
                {isSaving ? 'Saving...' : 'Save clips'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
